import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Button,
  Divider,
  FormHelperText,
  Grid,
  Icon,
  LinearProgress,
  Snackbar,
  TextField,
  Typography,
  Switch
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import MuiAlert from '@material-ui/lab/Alert';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import {
  uploadPrivateImageApi,
  getAssetManagerImagesApi,
  getAssetManagerTagByCodeApi
} from 'api';
import {
  changeHomepagePremiumCTAConfig,
  changeHomepagePremiumCTACategory
} from 'redux/actions/store_configs';
import {
  uploadFileTypeUnsupported,
  uploadFileSizeLimit,
  uploadImageResolutionLimit
} from 'redux/actions/general';
import { ImageGalleryDialog } from 'components';
import { assetManagerUrl } from 'configs';


const MAX_PREMIUM_CTA_ITEM_FILE_SIZE = 250 * 1024;
const PREMIUM_CTA_ITEM_IMAGE_WIDTH = 249;
const PREMIUM_CTA_ITEM_IMAGE_HEIGHT = 220;

const PremiumCTAItem = ({values, errors, setFieldValue, type}) => {
  const dispatch = useDispatch();
  const [openOwnImageDialog, toggleOpenOwnImageDialog] = useState(false);
  const [ownImagesTagIds, setOwnImagesTagIds] = useState('');

  const {
    storeActiveCategoriesWithActiveNavLevel,
    loadingCategories
  } = useSelector((state) => state.categories);

  const {
    assetCategoryTags,
    assetImagesData,
    currentCategoryId
  } = useSelector((state) => state.asset_manager);

  const {profile: {stores = []}} = useSelector((state) => state.user);

  const storeCode = stores.length && stores[0]?.storeCode;

  const imagePath = `store_info/premium_plus_cta/premium_plus_cta_${type}_item/premium_plus_cta_${type}_item_image`;
  const itemTagCode = `premium_plus_cta_${type}_item`;

  const itemTagData = assetCategoryTags.find((tag) => tag.tagCode === itemTagCode);
  const privateTagData = assetCategoryTags.find((tag) => tag.tagCode === 'private');
  const storeCodeTagData = assetCategoryTags.find((tag) => tag.tagCode === `${storeCode}`);

  useEffect(() => {
    if (itemTagData && privateTagData && storeCodeTagData) {
      setOwnImagesTagIds(`${itemTagData.id}, ${privateTagData.id}, ${storeCodeTagData.id}`);
    }
  }, [itemTagData, privateTagData, storeCodeTagData]);

  useEffect(() => {
    if (ownImagesTagIds) {
      getAssetManagerImagesApi({
        dispatch,
        limit: 20,
        page: 1,
        tagIds: ownImagesTagIds,
        isPrivate: true
      });
    }
  }, [ownImagesTagIds]);

  const handleChange = (event) => {
    const {name, checked, type: valueType, value} = event.target;
    dispatch(changeHomepagePremiumCTAConfig(name, checked, valueType, value, type));
    let fieldValue = value;

    if (valueType === 'checkbox') {
      fieldValue = checked ? '1' : '0';
    }

    setFieldValue(`${type}.[${name}]`, fieldValue);
  };

  const handleSelectCategory = (value, actionType, configTab, configPath) => {
    dispatch(changeHomepagePremiumCTACategory(actionType, value, configTab, configPath));
    setFieldValue(`[${type}].[${configPath}]`, value ? value.id : '');
  };

  const handleSelectImage = (data) => {
    const value = `${assetManagerUrl}/${data.webPath}`;
    dispatch(changeHomepagePremiumCTAConfig(imagePath, null, 'text', value, type));
    setFieldValue(`${type}.[${imagePath}]`, value);
  };

  const resolveUploadTag = async (localTag, tagCode, label) => {
    if (localTag) {
      return localTag.tagCode;
    }

    const existingTag = await getAssetManagerTagByCodeApi({dispatch, tagCode});

    return existingTag ? existingTag.tagCode : {label, tagCode, category: currentCategoryId};
  };

  const handleUpLoadImage = async (event) => {
    const {files, value} = event.target;

    if (files.length) {
      const fileTypeDotIndexPosition = value.lastIndexOf('.') + 1;
      const slicedFileTypeFromFilePath = value.slice(fileTypeDotIndexPosition);
      const acceptableFormats = 'png|jpeg';
      const hasImageFile = new RegExp(`(image\\/(${acceptableFormats}))`, 'gi');

      const reader = new FileReader();

      reader.readAsDataURL(files[0]);
      reader.onload = (e) => {
        const image = new Image();

        image.src = e.target.result;
        image.onload = async () => {
          const {height, width} = image;

          if (!hasImageFile.test(files[0].type) && !hasImageFile.test(slicedFileTypeFromFilePath)) {
            dispatch(uploadFileTypeUnsupported());
          } else if (width !== PREMIUM_CTA_ITEM_IMAGE_WIDTH || height !== PREMIUM_CTA_ITEM_IMAGE_HEIGHT) {
            dispatch(uploadImageResolutionLimit(`${PREMIUM_CTA_ITEM_IMAGE_WIDTH} x ${PREMIUM_CTA_ITEM_IMAGE_HEIGHT}px`));
          } else if (files[0].size > MAX_PREMIUM_CTA_ITEM_FILE_SIZE) {
            dispatch(uploadFileSizeLimit('250kB'));
          } else {
            const formData = new FormData();
            formData.append('image', files[0]);

            const isAddingNewTag = !ownImagesTagIds.length;

            if (isAddingNewTag) {
              const tags = [
                await resolveUploadTag(itemTagData, itemTagCode, itemTagCode),
                privateTagData ? privateTagData.tagCode : 'private',
                await resolveUploadTag(storeCodeTagData, storeCode, storeCode)
              ];
              formData.append('tags', JSON.stringify(tags));
            } else {
              formData.append('tags', `${itemTagCode},private,${storeCode}`);
            }

            const uploadedData = await uploadPrivateImageApi({
              dispatch,
              formData,
              isAddingNewTag,
              categoryName: 'Storebuild-automaton'
            });

            if (uploadedData) {
              handleSelectImage(uploadedData);
            }
          }
        };
      };
    }
  };

  const isCategoryError = errors?.[`${type}`]?.[`store_info/premium_plus_cta/premium_plus_cta_${type}_item/premium_plus_cta_${type}_item_category`];
  const isImageError = errors?.[`${type}`]?.[`store_info/premium_plus_cta/premium_plus_cta_${type}_item/premium_plus_cta_${type}_item_image`];
  const isTitleError = errors?.[`${type}`]?.[`store_info/premium_plus_cta/premium_plus_cta_${type}_item/premium_plus_cta_${type}_item_title`];
  const isLinkUrlError = errors?.[`${type}`]?.[`store_info/premium_plus_cta/premium_plus_cta_${type}_item/premium_plus_cta_${type}_item_redirect`];

  return (
    <>
      <Typography variant="h4" className="my-6 mx-3 capitalize">{type} CTA Item</Typography>
      <Divider />
      <Grid container spacing={3} className="my-8">
        <Grid item xs={12} sm={2} className="my-2 ml-4">Enable</Grid>
        <Grid item xs={12} sm={9}>
          <Switch
            color="primary"
            name={`store_info/premium_plus_cta/premium_plus_cta_${type}_item/is_premium_plus_cta_${type}_enable`}
            checked={values[`store_info/premium_plus_cta/premium_plus_cta_${type}_item/is_premium_plus_cta_${type}_enable`] == 1}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={2} className="ml-4">Select Category<span className="text-error">*</span></Grid>
        <Grid item xs={12} sm={9}>
          {loadingCategories && <LinearProgress className="mt-4" />}
          {!loadingCategories && !!storeActiveCategoriesWithActiveNavLevel.length &&
            <Autocomplete
              fullWidth
              size="small"
              options={storeActiveCategoriesWithActiveNavLevel}
              getOptionLabel={(option) => option.nameWithPath || ''}
              value={storeActiveCategoriesWithActiveNavLevel.find((category) => (
                category.id == values[`store_info/premium_plus_cta/premium_plus_cta_${type}_item/premium_plus_cta_${type}_item_category`]) || ''
              )}
              getOptionSelected={(option) => (
                values[`store_info/premium_plus_cta/premium_plus_cta_${type}_item/premium_plus_cta_${type}_item_category`] == option.id
              )}
              onChange={(_, value, action) => (
                handleSelectCategory(
                  value,
                  action,
                  type,
                  `store_info/premium_plus_cta/premium_plus_cta_${type}_item/premium_plus_cta_${type}_item_category`
                )
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  variant="outlined"
                  label="Category"
                  helperText={isCategoryError}
                  error={Boolean(isCategoryError)}
                />
              )}
              renderOption={(option, { inputValue }) => {
                const matches = match(option.nameWithPath, inputValue);
                const parts = parse(option.nameWithPath, matches);
                return (
                  <div>
                    {parts.map((part, index) => (
                      <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                        {part.text}
                      </span>
                    ))}
                  </div>
                );
              }}
            />
          }
        </Grid>
        <Grid item xs={12} sm={2} className="ml-4">Category Image<span className="text-error">*</span></Grid>
        <Grid item xs={12} sm={9}>
          {values[imagePath] &&
            <div className="mb-2">
              <img src={values[imagePath]} alt={`${type} CTA item`} className="max-w-300 max-h-150" />
            </div>
          }
          <div>
            <Button
              className="w-full max-w-300 p-0"
              variant="outlined"
              color="primary"
              size="large"
            >
              <label htmlFor={`upload-premium-cta-${type}-image`} className="flex items-center w-full justify-center px-5 py-2">
                <Icon className="mr-2">upload</Icon>
                Upload Own Image
              </label>
            </Button>
            <input
              className="hidden"
              onChange={handleUpLoadImage}
              onClick={(event) => (event.currentTarget.value = '')}
              id={`upload-premium-cta-${type}-image`}
              type="file"
              accept="image/png, image/jpeg"
            />
          </div>
          <FormHelperText className="mt-2">
            <div>Files supported: png, jpeg, jpg. File size should be less than 250kB.</div>
            <div>Image resolution should be exactly {PREMIUM_CTA_ITEM_IMAGE_WIDTH}x{PREMIUM_CTA_ITEM_IMAGE_HEIGHT}px.</div>
          </FormHelperText>
          <Button
            className="w-full max-w-300 mt-2 mr-2 bg-periwinkle-gray"
            variant="outlined"
            color="primary"
            size="large"
            onClick={() => toggleOpenOwnImageDialog(true)}
          >
            <Icon className="mr-2">wallpaper</Icon>
            Select From Own Images
          </Button>
          {isImageError &&
            <FormHelperText error className="mt-2">{isImageError}</FormHelperText>
          }
        </Grid>
        <Grid item xs={12} sm={2} className="ml-4">Category Title<span className="text-error">*</span></Grid>
        <Grid item xs={12} sm={9}>
          <TextField
            required
            fullWidth
            variant="outlined"
            name={`store_info/premium_plus_cta/premium_plus_cta_${type}_item/premium_plus_cta_${type}_item_title`}
            label="Title"
            value={values[`store_info/premium_plus_cta/premium_plus_cta_${type}_item/premium_plus_cta_${type}_item_title`]}
            onChange={handleChange}
            inputProps={{
              shrink: 'true'
            }}
            helperText={isTitleError}
            error={Boolean(isTitleError)}
          />
        </Grid>
        <Grid item xs={12} sm={2} className="ml-4">Button Link Redirect</Grid>
        <Grid item xs={12} sm={9}>
          <TextField
            fullWidth
            variant="outlined"
            name={`store_info/premium_plus_cta/premium_plus_cta_${type}_item/premium_plus_cta_${type}_item_redirect`}
            label="Link Redirect"
            value={values[`store_info/premium_plus_cta/premium_plus_cta_${type}_item/premium_plus_cta_${type}_item_redirect`]}
            onChange={handleChange}
            inputProps={{
              shrink: 'true'
            }}
            helperText={Boolean(isLinkUrlError) ? isLinkUrlError : 'Use this field to change Category button link'}
            error={Boolean(isLinkUrlError)}
          />
        </Grid>
      </Grid>
      {(ownImagesTagIds && openOwnImageDialog && assetImagesData[ownImagesTagIds]?.items?.length) ?
        <ImageGalleryDialog
          title={`Select Premium CTA ${type} Item Image From Own Images`}
          open={openOwnImageDialog}
          setOpen={toggleOpenOwnImageDialog}
          onImageSelect={handleSelectImage}
          imageTagId={ownImagesTagIds}
          isPrivate
          cols={4}
          rowHeight={340}
        />
        :
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          autoHideDuration={3000}
          onClose={() => toggleOpenOwnImageDialog(false)}
          open={openOwnImageDialog}
        >
          <MuiAlert elevation={6} variant="filled" severity="warning">
            You have no your own uploaded images.
            Firstly, you have to upload at least one image.
          </MuiAlert>
        </Snackbar>
      }
    </>
  );
};

export default PremiumCTAItem;
